import React, { useState } from "react";
import ChatIcon from "@/assets/icons/chat.svg";
import LikeIcon from "@/assets/icons/like.svg";
import LikedIcon from "@/assets/icons/liked.svg";
import { CommentInput } from "../Input";
import { CommentDto } from "@/api/comments/types";
import { Nft } from "@/api/nfts/types";
import { CommentLikeState } from "./types";
import { twMerge } from "tailwind-merge";
import { CommentAuthor } from "./CommentAuthor";

export interface Props {
  comment: CommentDto;
  nft?: Nft;
  onPost: (text: string) => Promise<CommentDto>;
  onDelete: (id: string) => void;
  replyTo?: string;
  likeState: CommentLikeState;
  showDisplayName?: boolean;
  className?: string;
  iconsSize?: "small" | "big";
  disabled?: boolean;
}

export default function Comment({
  comment,
  nft,
  onPost,
  onDelete,
  replyTo,
  likeState,
  showDisplayName,
  className,
  iconsSize = "small",
  disabled = false,
}: Props) {
  const [displayReplyField, setDisplayReplyField] = useState<boolean>(false);

  const { isLikedByMe, toggleLike, likeCount } = likeState;

  return (
    <>
      <div
        className={twMerge(
          "border-b border-gray-100 endemic:border-[rgba(143,145,153,0.5)] py-5 pl-4 pr-6",
          className,
        )}
      >
        <CommentAuthor
          commentId={comment.id}
          author={comment.author}
          createdAt={comment.createdAt}
          nft={nft}
          showDisplayName={showDisplayName}
          onDelete={onDelete}
        />
        <div className="ml-8 lg:ml-14">
          <div className="flex flex-row gap-x-2 bg-comment night:bg-[#0D0D0D] endemic:bg-transparent rounded-3xl p-5 text-body-3 leading-[22px] endemic:border endemic:rounded-3xl endemic:border-white">
            <span className="text-red font-bold">{replyTo || ""}</span>
            <p>{comment.text}</p>
          </div>
          <div className="flex justify-between pt-4 pb-2 text-body-4 ml-2 items-center">
            <div className="text-red endemic:text-primary flex flex-row items-center">
              {!comment.repliedToCommentId && !replyTo && (
                <span
                  onClick={() => {
                    if (disabled) return;

                    setDisplayReplyField(!displayReplyField);
                  }}
                  className={twMerge(
                    "lex mr-4 cursor-pointer items-center",
                    disabled && "cursor-auto",
                  )}
                >
                  <ChatIcon
                    className={twMerge(
                      "fill-red endemic:fill-primary mr-1",
                      iconsSize === "small" && "w-4",
                      iconsSize === "big" && "w-6",
                      disabled && "fill-endemicGray-700 endemic:opacity-60",
                    )}
                  />{" "}
                </span>
              )}
              <span
                onClick={() => {
                  if (disabled) return;

                  toggleLike();
                }}
                className={twMerge(
                  "flex cursor-pointer items-center",
                  disabled && "cursor-auto",
                )}
              >
                {isLikedByMe ? (
                  <>
                    <LikedIcon
                      className={twMerge(
                        "fill-red endemic:fill-primary mr-1",
                        iconsSize === "small" && "w-4",
                        iconsSize === "big" && "w-6",
                        disabled && "fill-endemicGray-700 endemic:opacity-60",
                      )}
                    />{" "}
                    <span
                      className={twMerge(
                        "hidden md:inline endemic:fill-primary",
                        disabled && "text-endemicGray-700 endemic:opacity-60",
                      )}
                    >
                      {likeCount && likeCount > 0 && likeCount}
                    </span>
                  </>
                ) : (
                  <>
                    <LikeIcon
                      className={twMerge(
                        "fill-red endemic:fill-primary mr-1",
                        iconsSize === "small" && "w-4",
                        iconsSize === "big" && "w-6",
                        disabled && "fill-endemicGray-700 endemic:opacity-60",
                      )}
                    />{" "}
                    <span
                      className={twMerge(
                        "hidden md:inline endemic:text-primary",
                        disabled && "text-endemicGray-700 endemic:opacity-60",
                      )}
                    >
                      {likeCount && likeCount > 0 && likeCount}
                    </span>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      {displayReplyField && (
        <CommentInput
          className="pl-14"
          iconsSize={iconsSize}
          onCommentPost={async (text: string) => {
            await onPost(text);

            setDisplayReplyField(false);
          }}
          replyText="Post"
        />
      )}
    </>
  );
}
